import { useEffect } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { apiGet } from "@/api/client";
import { useAuthStore } from "@/store/auth-store";
import { deriveAcademicYear } from "@/store/academic-year-store";

// ── Active child — one selection, every family screen ────────────────────────
//
// CAMP-2026-0535 / 0533 / 0536 / 0562 / 0565.
//
// A parent with two kids used to meet a different child selector on every
// screen, each with its own `useState` and its own default. Switching to the
// second child on the dashboard and then opening Fees put you back on the
// first; the diary had no selector at all; and the homework modal decided how
// many children you had by counting submission rows, which under-counts the
// moment a sibling sits in another class.
//
// This module is the single source of truth:
//   • `useChildren()`   — the canonical list, from GET /me/children.
//   • the zustand store — the selected id, persisted so it survives a reload
//                         and a native cold start.
//   • `useActiveChild()` — what screens actually consume. It reconciles the
//                         two: if the persisted id isn't in the list (child
//                         graduated, parent switched school, first ever run)
//                         it falls back to the first child and writes that
//                         back, so `activeChildId` is never a dangling id.
//
// Screens must not keep their own copy of "selected child" any more. Read
// `activeChildId`, render `<ChildSwitcher/>`, done.

export interface ActiveChild {
  id: string;
  name: string;
  avatar: string | null;
  admissionNo: string;
  rollNo: string | null;
  class: { id: string; name: string; grade: number } | null;
  section: string | null;
  status: string;
  schoolId: string;
  /** ISO date the student joined the school. CAMP-2026-0769. */
  joinedAt?: string | null;
  /** Academic year label of the class they sit in, e.g. "2026-27". */
  academic?: string | null;
}

/**
 * The academic years a family has any business looking at.
 *
 * CAMP-2026-0769 — the year switcher offers the school's whole window
 * (previous year through +3). For a parent whose child joined this year that
 * means a "Previous Year" option with nothing behind it, and three future
 * years that cannot hold anything either; picking one blanks every card on the
 * dashboard. The floor is the year the earliest-joining child arrived, and the
 * ceiling is the current year — a family's history cannot start before their
 * child did, and it cannot run ahead of today.
 *
 * Returns an empty array when the roster is not loaded yet, which callers read
 * as "no restriction" — the switcher must never be narrowed on missing data.
 */
export function familyAcademicYears(
  children: ActiveChild[],
  current = deriveAcademicYear(),
): string[] {
  if (children.length === 0) return [];
  const currentStart = Number.parseInt(current.slice(0, 4), 10);
  if (!Number.isFinite(currentStart)) return [];

  const starts: number[] = [];
  for (const child of children) {
    // The class's own label is the most direct statement of which year the
    // student is enrolled for; `joinedAt` covers a child with no class yet.
    const fromClass = child.academic ? Number.parseInt(child.academic.slice(0, 4), 10) : NaN;
    if (Number.isFinite(fromClass)) starts.push(fromClass);
    if (child.joinedAt) {
      const joined = new Date(child.joinedAt);
      if (!Number.isNaN(joined.getTime())) {
        starts.push(Number.parseInt(deriveAcademicYear(joined).slice(0, 4), 10));
      }
    }
  }
  if (starts.length === 0) return [];

  const floor = Math.min(...starts, currentStart);
  const out: string[] = [];
  for (let y = floor; y <= currentStart; y++) {
    out.push(`${y}-${String(y + 1).slice(-2)}`);
  }
  return out;
}

export interface ChildrenResponse {
  children: ActiveChild[];
}

/**
 * The caller's children. PARENT → their kids; STUDENT → themselves; any other
 * role → an empty list (the endpoint answers 200 with `[]` rather than 403 so
 * this hook is safe to call unconditionally from shared layout code).
 */
export const childrenQueryKey = (activeSchoolId: string | null | undefined) => ["me", "children", activeSchoolId] as const;

export function useChildren(): UseQueryResult<ChildrenResponse> {
  const role = useAuthStore((s) => s.user?.role);
  const activeSchoolId = useAuthStore((s) => s.activeSchoolId);
  const isFamily = role === "PARENT" || role === "STUDENT";
  return useQuery({
    queryKey: childrenQueryKey(activeSchoolId),
    queryFn: () => apiGet<ChildrenResponse>("/me/children"),
    enabled: isFamily,
    // The roster changes at admission time, not during a session.
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

interface ActiveChildState {
  /** Raw persisted selection. Prefer `useActiveChild().activeChildId`, which
   *  is reconciled against the actual roster. */
  selectedChildId: string | null;
  setSelectedChildId: (id: string | null) => void;
}

export const useActiveChildStore = create<ActiveChildState>()(
  persist(
    (set) => ({
      selectedChildId: null,
      setSelectedChildId: (id) => set({ selectedChildId: id }),
    }),
    { name: "campushubai-active-child" },
  ),
);

export interface UseActiveChildResult {
  /** Full roster, in the API's order (class then name). */
  children: ActiveChild[];
  /** The selected child, or null while the roster is still loading. */
  activeChild: ActiveChild | null;
  /**
   * The selected child's id, guaranteed to exist in `children` once loaded.
   * `undefined` (not null) while loading, so it can be passed straight to a
   * query hook whose `?childId=` param should be omitted until we know.
   */
  activeChildId: string | undefined;
  setActiveChildId: (id: string) => void;
  /** True when a switcher is worth rendering. */
  hasMultiple: boolean;
  isLoading: boolean;
  isError: boolean;
}

export function useActiveChild(): UseActiveChildResult {
  const { data, isLoading, isError } = useChildren();
  const selectedChildId = useActiveChildStore((s) => s.selectedChildId);
  const setSelectedChildId = useActiveChildStore((s) => s.setSelectedChildId);

  const children = data?.children ?? [];
  const match = children.find((c) => c.id === selectedChildId) ?? null;
  const resolved = match ?? children[0] ?? null;

  // Heal a dangling selection. Guarded on "we actually have a roster" so a
  // loading or errored fetch never clears a perfectly good stored id.
  useEffect(() => {
    if (children.length === 0) return;
    if (resolved && resolved.id !== selectedChildId) setSelectedChildId(resolved.id);
  }, [children.length, resolved, selectedChildId, setSelectedChildId]);

  return {
    children,
    activeChild: resolved,
    activeChildId: resolved?.id,
    setActiveChildId: setSelectedChildId,
    hasMultiple: children.length > 1,
    isLoading,
    isError,
  };
}

/** "Aarav Sharma — Class 6 A" — the label every switcher shows. Siblings who
 *  share a first name are only distinguishable with the class attached, which
 *  is what CAMP-2026-0533 was filed about. */
export function childLabel(c: Pick<ActiveChild, "name" | "class" | "section">): string {
  const cls = c.class?.name
    ? `${c.class.name}${c.section ? ` ${c.section}` : ""}`
    : null;
  return cls ? `${c.name} — ${cls}` : c.name;
}
